// letter-pile.js — Matter.js physics with canvas-sampled convex hull bodies.
// Each letter's collision shape is derived from its actual rendered pixel outline,
// not its bounding rectangle, so letters can nestle together naturally.
// Requires: poly-decomp, then matter-js, then this script (all deferred).

(function () {
  'use strict';

  if (typeof Matter === 'undefined') {
    console.warn('letter-pile: Matter.js not found');
    return;
  }

  // Register poly-decomp if present so Bodies.fromVertices can handle
  // any shape correctly (even though our hulls are always convex).
  if (window.decomp && Matter.Common && Matter.Common.setDecomp) {
    Matter.Common.setDecomp(window.decomp);
  }

  var Engine    = Matter.Engine;
  var Bodies    = Matter.Bodies;
  var Body      = Matter.Body;
  var Composite = Matter.Composite;

  // ------------------------------------------------------------------ //
  //  Font catalogue
  // ------------------------------------------------------------------ //
  // hullScale: [thinScale, boldScale]
  // Ranges are intentionally narrow for most fonts — geometric/display thin
  // letters are outline shapes that already fill their bounding boxes, so an
  // aggressive thin reduction just causes visual overlap (physics body << letter).
  // Imbue is the exception: genuine hairline strokes warrant a wider swing.
  var FONTS = {
    'Anybody':      { wght: [100, 900], page: '/pages/anybody',      hullScale: [0.86, 0.93] },
    'Epilogue':     { wght: [100, 900], page: '/pages/epilogue',      hullScale: [0.83, 0.91] },
    'Gluten':       { wght: [100, 900], page: '/pages/gluten',        hullScale: [0.80, 0.88] },
    'Grandstander': { wght: [100, 900], page: '/pages/grandstander',  hullScale: [0.84, 0.91] },
    'Imbue':        { wght: [100, 900], page: '/pages/imbue',         hullScale: [0.66, 0.85] },
    'Sono':         { wght: [200, 800], page: '/pages/sono',          hullScale: [0.82, 0.89] },
    'Tourney':      { wght: [100, 900], page: '/pages/tourney',       hullScale: [0.86, 0.93] },
    'Trispace':     { wght: [100, 800], page: '/pages/trispace',      hullScale: [0.86, 0.93] }
  };

  // wl = normalised weight position: 0.0 = font min, 1.0 = font max.
  // Quarter-stops (0.25, 0.75) give the two extra ETC sets their own weights.
  var LETTERS = [
    { char: 'E', wl: 0.5,  angle:   0 },
    { char: 'e', wl: 0.5,  angle:   0 },
    { char: 'T', wl: 0.0,  angle:  15 },
    { char: 'T', wl: 1.0,  angle:   0 },
    { char: 'c', wl: 0.5,  angle:   8 },
    { char: 'C', wl: 0.5,  angle: -10 },
    { char: 'C', wl: 1.0,  angle:   0 },
    { char: 'c', wl: 1.0,  angle:  -5 },
    { char: 'e', wl: 1.0,  angle:  -9 },
    { char: 'E', wl: 1.0,  angle:   0 },
    { char: 'T', wl: 0.5,  angle:   6 },
    { char: 't', wl: 1.0,  angle:   0 },
    { char: 't', wl: 0.0,  angle:  15 },
    { char: 'T', wl: 0.0,  angle:   6 },
    { char: 'c', wl: 0.0,  angle:   0 },
    { char: 'C', wl: 0.0,  angle: -18 },
    { char: 'E', wl: 0.0,  angle:   0 },
    { char: 'e', wl: 0.0,  angle:   0 },
    // Two extra ETC sets at quarter-point weights not shown elsewhere
    { char: 'E', wl: 0.25, angle: -12 },
    { char: 'T', wl: 0.25, angle:  22 },
    { char: 'C', wl: 0.25, angle:  -6 },
    { char: 'E', wl: 0.75, angle:  10 },
    { char: 'T', wl: 0.75, angle: -20 },
    { char: 'C', wl: 0.75, angle:  17 }
  ];

  // ------------------------------------------------------------------ //
  //  Physics constants
  // ------------------------------------------------------------------ //
  // Air friction is mode-dependent (see setGravity / createLetterBodies):
  //   gravity modes → 0.018  so letters accelerate freely and feel weighty
  //   none mode     → 0.055  tighter damping keeps the floating pile controlled
  var FRICTION_AIR_GRAVITY = 0.018;
  var FRICTION_AIR_NONE    = 0.042;
  var FRICTION      = 0.55;   // surface friction between letters
  var RESTITUTION   = 0.12;   // small but noticeable bounce on impact
  var DENSITY       = 0.002;
  var WALL_THICK    = 300;
  var SPRING        = 0.00022; // centering spring strength (none-gravity mode)

  // ------------------------------------------------------------------ //
  //  Canvas shape sampling
  //  Renders each letter to an offscreen canvas at RENDER_PX, samples
  //  the alpha channel to build a point cloud, then computes the convex
  //  hull. Returns vertices scaled to the actual target font size.
  // ------------------------------------------------------------------ //
  var RENDER_PX  = 150; // canvas render size for measurement
  var HULL_STEP  = 4;   // sample every N pixels (smaller = finer hull)
  var shapeCache = {};  // keyed by "char|font|wght"

  // Andrew's monotone chain convex hull, O(n log n)
  function cross(O, A, B) {
    return (A.x - O.x) * (B.y - O.y) - (A.y - O.y) * (B.x - O.x);
  }

  function convexHull(pts) {
    pts = pts.slice().sort(function (a, b) {
      return a.x !== b.x ? a.x - b.x : a.y - b.y;
    });
    var n = pts.length;
    if (n < 3) return pts;
    var lo = [], hi = [], i;
    for (i = 0; i < n; i++) {
      while (lo.length >= 2 && cross(lo[lo.length - 2], lo[lo.length - 1], pts[i]) <= 0) lo.pop();
      lo.push(pts[i]);
    }
    for (i = n - 1; i >= 0; i--) {
      while (hi.length >= 2 && cross(hi[hi.length - 2], hi[hi.length - 1], pts[i]) <= 0) hi.pop();
      hi.push(pts[i]);
    }
    lo.pop(); hi.pop();
    return lo.concat(hi);
  }

  // Sample a letter's pixel outline and return a descriptor:
  //   { verts, vw, vh }
  //   verts — convex hull vertices centered at centroid, scaled to targetPx
  //   vw/vh — visual bounding box at targetPx (for DOM translate)
  function measureShape(char, font, wght, targetPx, hullScale) {
    var hs  = (hullScale !== undefined) ? hullScale : 1.0;
    var key = char + '|' + font + '|' + wght + '|' + hs;
    if (shapeCache[key]) {
      var cached = shapeCache[key];
      var s2 = targetPx / RENDER_PX;
      return {
        verts: cached.verts.map(function (p) { return { x: p.x * s2, y: p.y * s2 }; }),
        vw: cached.vw * s2,
        vh: cached.vh * s2
      };
    }

    var RS  = RENDER_PX;
    var PAD = 12;
    var CW  = Math.ceil(RS * 1.5 + PAD * 2);
    var CH  = Math.ceil(RS * 1.5 + PAD * 2);
    var cvs = document.createElement('canvas');
    cvs.width = CW; cvs.height = CH;
    var ctx = cvs.getContext('2d');

    ctx.font = wght + ' ' + RS + 'px "' + font + '"';
    ctx.fillStyle = '#fff';
    // Draw at baseline = 90% down the canvas
    ctx.fillText(char, PAD, RS + PAD);

    var data = ctx.getImageData(0, 0, CW, CH).data;
    var pts  = [];
    for (var y = 0; y < CH; y += HULL_STEP) {
      for (var x = 0; x < CW; x += HULL_STEP) {
        if (data[(y * CW + x) * 4 + 3] > 60) {
          pts.push({ x: x - PAD, y: y - PAD });
        }
      }
    }

    if (pts.length < 6) return null; // canvas couldn't render font yet

    var hull = convexHull(pts);

    // Bounding box
    var minX = hull[0].x, maxX = hull[0].x, minY = hull[0].y, maxY = hull[0].y;
    hull.forEach(function (p) {
      if (p.x < minX) minX = p.x; if (p.x > maxX) maxX = p.x;
      if (p.y < minY) minY = p.y; if (p.y > maxY) maxY = p.y;
    });

    // Centroid of hull (simple average of vertices)
    var cx = 0, cy = 0;
    hull.forEach(function (p) { cx += p.x; cy += p.y; });
    cx /= hull.length; cy /= hull.length;

    // Vertices centered at origin, scaled by hullScale so the physics body
    // is slightly smaller than the convex hull — compensates for concave
    // letterforms (c, e, C, E) whose hulls overshoot their ink area.
    var centeredVerts = hull.map(function (p) {
      return { x: (p.x - cx) * hs, y: (p.y - cy) * hs };
    });

    // Cache at render scale, return scaled to targetPx
    shapeCache[key] = {
      verts: centeredVerts,
      vw: maxX - minX,
      vh: maxY - minY
    };

    var scale = targetPx / RENDER_PX;
    return {
      verts: centeredVerts.map(function (p) { return { x: p.x * scale, y: p.y * scale }; }),
      vw: (maxX - minX) * scale,
      vh: (maxY - minY) * scale
    };
  }

  // ------------------------------------------------------------------ //
  //  State
  // ------------------------------------------------------------------ //
  var section, stage, tooltip, tooltipText;
  var letterEls    = [];
  var engine, world;
  var letterBodies = [];
  var wallBodies   = [];
  var stageW = 0, stageH = 0;
  var gravMode     = 'none';
  var currentFont  = 'Epilogue';
  var rafId        = null;
  var reduced      = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function rand(a, b)       { return a + Math.random() * (b - a); }
  function clamp(v, lo, hi) { return Math.max(lo, Math.min(hi, v)); }
  function rad2deg(r)       { return r * 180 / Math.PI; }
  function deg2rad(d)       { return d * Math.PI / 180; }

  // wl is now a 0.0–1.0 normalised position across the font's weight range.
  function wghtForFont(font, wl) {
    var r = FONTS[font].wght;
    return Math.round(r[0] + (r[1] - r[0]) * wl);
  }

  // Interpolate hullScale across the [thin, bold] range.
  function hullScaleForLevel(font, wl) {
    var hs = FONTS[font].hullScale;
    return hs[0] + (hs[1] - hs[0]) * wl;
  }

  function fontSize() {
    return clamp(stageW * 0.170, 76, 252);
  }

  // ------------------------------------------------------------------ //
  //  DOM
  // ------------------------------------------------------------------ //
  function buildLetters() {
    letterEls = [];
    stage.innerHTML = '';

    LETTERS.forEach(function (def) {
      var wght = wghtForFont(currentFont, def.wl);
      var el   = document.createElement('a');
      el.className     = 'lp-letter';
      el.href          = FONTS[currentFont].page;
      el.dataset.wght  = wght;
      el.dataset.wl    = def.wl;
      el.setAttribute('aria-label', def.char + ' — ' + currentFont + ', weight ' + wght);

      var span = document.createElement('span');
      span.textContent = def.char;
      el.appendChild(span);

      applyFontStyle(el, currentFont, wght);
      stage.appendChild(el);
      letterEls.push(el);
    });
  }

  function applyFontStyle(el, font, wght) {
    var span = el.querySelector('span');
    span.style.fontFamily            = "'" + font + "', sans-serif";
    span.style.fontVariationSettings = '"wght" ' + wght;
    span.style.fontSize              = fontSize() + 'px';
    span.style.lineHeight            = '0.85';
  }

  // ------------------------------------------------------------------ //
  //  Physics — engine
  // ------------------------------------------------------------------ //
  function createEngine() {
    engine = Engine.create({
      // scale: 0.004 = 4× the Matter default, gives satisfying weight to falls
      gravity:            { x: 0, y: 0, scale: 0.004 },
      positionIterations: 12,
      velocityIterations: 10
    });
    world = engine.world;
  }

  function createWalls() {
    var T = WALL_THICK;
    wallBodies = [
      Bodies.rectangle(stageW / 2, stageH + T / 2, stageW * 3, T,
        { isStatic: true, friction: FRICTION, restitution: RESTITUTION }),
      Bodies.rectangle(stageW / 2, -T / 2, stageW * 3, T,
        { isStatic: true, friction: FRICTION, restitution: RESTITUTION }),
      Bodies.rectangle(-T / 2, stageH / 2, T, stageH * 3,
        { isStatic: true, friction: FRICTION, restitution: RESTITUTION }),
      Bodies.rectangle(stageW + T / 2, stageH / 2, T, stageH * 3,
        { isStatic: true, friction: FRICTION, restitution: RESTITUTION })
    ];
    Composite.add(world, wallBodies);
  }

  function createLetterBodies() {
    var cx  = stageW / 2;
    var cy  = stageH / 2;
    var fsx = fontSize();

    letterBodies = letterEls.map(function (el, i) {
      var def  = LETTERS[i];
      var wght = wghtForFont(currentFont, def.wl);
      var fs   = fsx;

      var x = cx + rand(-stageW * 0.32, stageW * 0.32);
      var y = cy + rand(-stageH * 0.26, stageH * 0.26);

      var opts = {
        frictionAir: gravMode === 'none' ? FRICTION_AIR_NONE : FRICTION_AIR_GRAVITY,
        friction:    FRICTION,
        restitution: RESTITUTION,
        density:     DENSITY,
        angle:       deg2rad(def.angle)
      };

      var shape = measureShape(def.char, currentFont, wght, fs, hullScaleForLevel(currentFont, def.wl));
      var body;

      if (shape && shape.verts.length >= 3 && Bodies.fromVertices) {
        try {
          body = Bodies.fromVertices(x, y, [shape.verts], opts, true);
          body._vw = shape.vw;
          body._vh = shape.vh;
        } catch (e) {
          shape = null;
        }
      }

      if (!shape || !body) {
        // Fallback: approximate rectangle from DOM measurement
        var span  = el.querySelector('span');
        var r     = span.getBoundingClientRect();
        var bw    = Math.max(r.width  - 8, 20);
        var bh    = Math.max(r.height - 8, 20);
        body      = Bodies.rectangle(x, y, bw, bh, opts);
        body._vw  = r.width  || bw;
        body._vh  = r.height || bh;
      }

      body._el = el;
      return body;
    });

    Composite.add(world, letterBodies);
  }

  // ------------------------------------------------------------------ //
  //  DOM ↔ physics sync
  //  body.position = center of mass ≈ visual center of letter.
  //  translate positions the element's top-left; CSS rotate(deg) then
  //  pivots around the element's own center (transform-origin: 50% 50%).
  // ------------------------------------------------------------------ //
  function syncDOM() {
    letterBodies.forEach(function (b) {
      var tx  = b.position.x - b._vw / 2;
      var ty  = b.position.y - b._vh / 2;
      var deg = rad2deg(b.angle);
      b._el.style.transform =
        'translate(' + tx.toFixed(1) + 'px,' + ty.toFixed(1) + 'px)' +
        ' rotate(' + deg.toFixed(2) + 'deg)';
    });
  }

  // ------------------------------------------------------------------ //
  //  Centering spring (none-gravity mode)
  // ------------------------------------------------------------------ //
  function applyCenteringForce() {
    var cx = stageW / 2;
    var cy = stageH / 2;
    letterBodies.forEach(function (b) {
      Body.applyForce(b, b.position, {
        x: (cx - b.position.x) * SPRING,
        y: (cy - b.position.y) * SPRING
      });
    });
  }

  // ------------------------------------------------------------------ //
  //  Main loop
  // ------------------------------------------------------------------ //
  function tick() {
    if (gravMode === 'none') applyCenteringForce();
    Engine.update(engine, 1000 / 60);
    syncDOM();
    rafId = requestAnimationFrame(tick);
  }

  // ------------------------------------------------------------------ //
  //  Gravity control
  // ------------------------------------------------------------------ //
  function setGravity(mode) {
    gravMode = mode;
    engine.gravity.y = mode === 'normal'  ?  1 :
                       mode === 'reverse' ? -1 : 0;
    engine.gravity.x = 0;

    // Swap air friction: gravity modes allow momentum to build freely;
    // none-mode keeps higher drag so the floating pile stays controlled.
    var airFric = mode === 'none' ? FRICTION_AIR_NONE : FRICTION_AIR_GRAVITY;

    letterBodies.forEach(function (b) {
      b.frictionAir = airFric;
      // Stronger kick so the mode change is immediately dramatic
      Body.applyForce(b, b.position, {
        x: rand(-0.012, 0.012),
        y: rand(-0.012, 0.012)
      });
      Body.setAngularVelocity(b, rand(-0.10, 0.10));
    });
  }

  // ------------------------------------------------------------------ //
  //  Font switch
  // ------------------------------------------------------------------ //
  function setFont(fontName) {
    if (!FONTS[fontName]) return;
    currentFont = fontName;

    letterBodies.forEach(function (b) { Composite.remove(world, b); });
    letterBodies = [];

    letterEls.forEach(function (el, i) {
      var def  = LETTERS[i];
      var wght = wghtForFont(fontName, def.wl);
      el.href         = FONTS[fontName].page;
      el.dataset.wght = wght;
      el.setAttribute('aria-label', def.char + ' — ' + fontName + ', weight ' + wght);
      applyFontStyle(el, fontName, wght);
    });

    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        createLetterBodies();
        letterBodies.forEach(function (b) {
          Body.setVelocity(b, { x: rand(-6, 6), y: rand(-6, 6) });
          Body.setAngularVelocity(b, rand(-0.15, 0.15));
        });
      });
    });
  }

  // ------------------------------------------------------------------ //
  //  Tooltip
  // ------------------------------------------------------------------ //
  function showTooltip(e, wght) {
    if (!tooltip) return;
    tooltip.setAttribute('aria-hidden', 'false');
    tooltipText.textContent = 'WGHT: ' + wght;
    var r = stage.getBoundingClientRect();
    tooltip.style.left = (e.clientX - r.left) + 'px';
    tooltip.style.top  = (e.clientY - r.top)  + 'px';
  }

  function hideTooltip() {
    if (tooltip) tooltip.setAttribute('aria-hidden', 'true');
  }

  function clearHovered() {
    stage.querySelectorAll('.lp-letter.is-hovered').forEach(function (l) {
      l.classList.remove('is-hovered');
    });
  }

  // ------------------------------------------------------------------ //
  //  Init
  // ------------------------------------------------------------------ //
  function init() {
    section     = document.querySelector('[data-letter-pile]');
    if (!section) return;

    stage       = section.querySelector('[data-lp-stage]');
    tooltip     = section.querySelector('[data-lp-tooltip]');
    tooltipText = section.querySelector('[data-lp-tooltip-text]');

    stageW = stage.offsetWidth;
    stageH = stage.offsetHeight;

    buildLetters();
    createEngine();
    createWalls();

    // Two frames to let fonts load into canvas context before measuring
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        createLetterBodies();
        syncDOM();
        if (!reduced) rafId = requestAnimationFrame(tick);
      });
    });

    // Gravity buttons
    section.querySelectorAll('[data-lp-gravity]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        section.querySelectorAll('[data-lp-gravity]').forEach(function (b) {
          b.classList.remove('is-active');
        });
        btn.classList.add('is-active');
        setGravity(btn.dataset.lpGravity);
      });
    });

    // Font buttons
    section.querySelectorAll('[data-lp-font]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        section.querySelectorAll('[data-lp-font]').forEach(function (b) {
          b.classList.remove('is-active');
        });
        btn.classList.add('is-active');
        setFont(btn.dataset.lpFont);
      });
    });

    // Hover / tooltip (delegated)
    stage.addEventListener('mousemove', function (e) {
      var el = e.target.closest('.lp-letter');
      if (!el) { hideTooltip(); clearHovered(); return; }
      clearHovered();
      el.classList.add('is-hovered');
      showTooltip(e, el.dataset.wght);
    });

    stage.addEventListener('mouseleave', function () {
      hideTooltip();
      clearHovered();
    });

    // Resize
    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        stageW = stage.offsetWidth;
        stageH = stage.offsetHeight;
        wallBodies.forEach(function (b) { Composite.remove(world, b); });
        createWalls();
        letterEls.forEach(function (el, i) {
          var def = LETTERS[i];
          applyFontStyle(el, currentFont, wghtForFont(currentFont, def.wl));
        });
      }, 150);
    });

    // Pause when tab hidden
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) {
        cancelAnimationFrame(rafId); rafId = null;
      } else if (!reduced) {
        rafId = requestAnimationFrame(tick);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
